import { makeStyles } from '@mui/styles'
import PageData from '@v4/utils/data/PageData'
import { isHtmlEmpty } from '@v4/hooks/useEditor/useEditor.utils'

const heading = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6']
const excludedColors = ['button', 'inherit']

const useStyles = makeStyles(
	theme => {
		const isWhite = ({ white, pageType }, withShadow = false) => {
			if (withShadow && PageData.isTypeResultPdf(pageType)) {
				return false
			}
			return white === true || (white === 'theme' && theme.palette.whiteText)
		}
		function createHeadingCSS() {
			const styles = {}

			for (let i = 1; i <= 6; i += 1) {
				const headingTag = `h${i}`
				const key = `& ${headingTag}`

				styles[key] = {
					...theme.typography[headingTag],
					color: props =>
						isWhite(props) ? '#FFFFFF' : theme.palette.getTextColors(props.usePaperColors).primary,
					marginBottom: theme.spacing(i <= 2 ? 2 : 1.5),
				}
				if (!theme.isPdf && i < 4) {
					styles[key]['& b, & strong'] = {
						fontWeight: 700,
					}
				}
			}

			return styles
		}

		const getLinkColor = ({ usePaperColors }) =>
			theme.palette.calcContrastColor(
				theme.palette.primary.main,
				usePaperColors ? theme.palette.background.paper : theme.palette.background.default,
			)

		const textShadowFrom = (props, withShadow) => {
			return isWhite(props, withShadow) || props.shadow ? `0 0 4px rgb(0 0 0 / ${props.textShadow}%)` : null
		}

		return {
			root: {
				width: '100%',
				maxWidth: '100%',
				overflowWrap: 'break-word',
				textDecoration: props =>
					(!_.includes(['body1', 'body2'], props.variant) && theme.typography.button.textDecoration) ||
					'none',
				color: props => {
					if (isWhite(props)) return '#FFFFFF'
					if (excludedColors.includes(props.variant)) return undefined
					const colors = theme.palette.getTextColors(props.usePaperColors)

					return heading.includes(props.variant) ? colors.primary : colors.secondary
				},
				textShadow: props => textShadowFrom(props, true),
				'& p': {
					fontSize: ({ variant = 'body2' }) => theme.typography[variant]?.fontSize || null,
					'& .p-primary': {
						fontSize: theme.isPdf ? theme.typography.subtitle2.fontSize : null,
					},
				},
				'& a': {
					color: props => (isWhite(props) ? '#FFFFFF' : getLinkColor(props)),
					textShadow: props => textShadowFrom(props, true),
					'&:hover, &:focus, &:active': {
						color: props => (isWhite(props) ? '#FFFFFF' : getLinkColor(props)),
						textDecoration: 'none',
					},
					'&:focus': {
						outlineColor: props => (isWhite(props) ? '#FFFFFF' : getLinkColor(props)),
					},
				},
				'& ul': theme.isPdf
					? {}
					: {
							padding: '0',
							'& li': {
								listStyle: 'none',
								position: 'relative',
								padding: theme.spacing(0.3, 0, 0.2, 3.666),
								paddingBottom: theme.spacing(1),
							},
							'& li::before': {
								position: 'absolute',
								left: 0,
								content: "'\\e008'",
								'-webkit-font-smoothing': 'antialiased',
								'-moz-osx-font-smoothing': 'grayscale',
								fontStyle: 'normal',
								fontSynthesis: 'none',
								fontVariant: 'normal',
								fontFamily: '"Font Awesome Kit"',
								fontSize: theme.spacing(2.8),
								top: theme.spacing(-0.3),
								color: props => (isWhite(props) ? '#FFFFFF' : theme.palette.primary.main),
								textShadow: props => textShadowFrom(props, true),
							},
					  },
				'& ol': {
					paddingLeft: theme.spacing(1.75),
				},
				'& ul, ol, p': {
					margin: '0 0 1em',
					textDecoration: ({ variant = 'body2' }) => theme.typography[variant]?.textDecoration || null,
				},
				'& > *:last-child': {
					marginBottom: 0,
				},
				'& b, & strong': {
					fontWeight: !theme.isPdf ? 500 : null,
				},
				...createHeadingCSS(),
				'& .p-primary': {
					color: props =>
						isWhite(props) ? '#FFFFFF' : theme.palette.getTextColors(props.usePaperColors).primary,
					fontWeight: 500,
				},
				'& img': {
					maxWidth: '100%',
				},
			},
			editable: {
				cursor: 'text',
				'&:focus': {
					minWidth: '80px',
				},
				'&:hover, &:focus-visible, &.mce-edit-focus': {
					outline: props => {
						const color = isWhite(props)
							? '#FFFFFF'
							: theme.palette.calcContrastColor(
									theme.palette.scoreapp.primary,
									props.usePaperColors
										? theme.palette.background.paper
										: theme.palette.background.default,
							  )
						return `1px dashed ${color}`
					},
					outlineOffset: -1,
				},

				'&[data-placeholder]': {
					position: 'relative',
					minWidth: props => (isHtmlEmpty(props.html) ? '80px' : '0'),
					'&::before': {
						content: props => (isHtmlEmpty(props.html) ? 'attr(data-placeholder)' : '""'),
						opacity: 1,
						color: props => {
							return isWhite(props)
								? '#FFFFFF'
								: theme.palette.calcContrastColor(
										theme.palette.scoreapp.placeholder,
										props.usePaperColors
											? theme.palette.background.paper
											: theme.palette.background.default,
								  )
						},
						position: 'absolute',
						left: 0,
						right: 0,
						bottom: 0,
						font: 'inherit',
						lineHeight: 'inherit',
						transition: 'opacity 0.1s',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
						direction: theme.direction || 'ltr',
					},
				},
			},
		}
	},
	{ name: 'EditableHTML' },
)

export default useStyles
